import type { FileItem, FileExplorerFilters, FileActionButton } from "../types";
import { formatDistanceToNow } from "date-fns";
import { apiConfig } from "@/shared/config/api";
import { Tag, Download, Trash2 } from "lucide-react";

export function extractUniqueValues<T>(
  files: FileItem[],
  extractor: (file: FileItem) => T | undefined
): T[] {
  const values = new Set<T>();
  files.forEach((file) => {
    const value = extractor(file);
    if (value !== undefined) {
      values.add(value);
    }
  });
  return Array.from(values);
}

export function extractUniqueTags(files: FileItem[]): string[] {
  const tags = new Set<string>();
  files.forEach((file) => {
    file.tags.forEach((tag) => tags.add(tag));
  });
  return Array.from(tags).sort();
}

const standardFilterKeys = new Set(["tags", "name"]);

const isFilterValuePresent = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return String(value).trim().length > 0;
};

export const normalizeFilterValue = (value: unknown): string | undefined => {
  if (!isFilterValuePresent(value)) {
    return undefined;
  }

  return String(value).trim();
};

export interface ActiveFilterChip {
  key: string;
  label: string;
  value: string;
  type: "tag" | "name" | "attribute";
  sourceKey?: string;
}

export function hasActiveFilters(filters: FileExplorerFilters): boolean {
  return deriveActiveFilterChips(filters).length > 0;
}

export function deriveActiveFilterChips(
  filters: FileExplorerFilters
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (isFilterValuePresent(filters.name)) {
    chips.push({
      key: "name",
      label: "Name",
      value: String(filters.name),
      type: "name",
    });
  }

  filters.tags.forEach((tag) => {
    if (!isFilterValuePresent(tag)) {
      return;
    }

    chips.push({
      key: `tag:${tag}`,
      label: "Tag",
      value: String(tag),
      type: "tag",
    });
  });

  Object.entries(filters).forEach(([key, value]) => {
    if (standardFilterKeys.has(key)) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((val, index) => {
        if (!isFilterValuePresent(val)) {
          return;
        }

        chips.push({
          key: `${key}:${index}`,
          label: key,
          value: String(val),
          type: "attribute",
          sourceKey: key,
        });
      });
      return;
    }

    if (isFilterValuePresent(value)) {
      chips.push({
        key,
        label: key,
        value: String(value),
        type: "attribute",
        sourceKey: key,
      });
    }
  });

  return chips;
}

export function countActiveFilters(filters: FileExplorerFilters): number {
  return deriveActiveFilterChips(filters).length;
}

const sanitizeFilters = (filters: FileExplorerFilters): FileExplorerFilters => {
  const cleanedTags = filters.tags
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);

  const nextFilters: FileExplorerFilters = {
    tags: cleanedTags,
  };

  const normalizedName = normalizeFilterValue(filters.name);
  if (normalizedName) {
    nextFilters.name = normalizedName;
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (standardFilterKeys.has(key)) {
      return;
    }

    if (Array.isArray(value)) {
      const filteredValues = value
        .map((val) => String(val).trim())
        .filter((val) => val.length > 0);
      if (filteredValues.length > 0) {
        nextFilters[key] = filteredValues;
      }
      return;
    }

    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (isFilterValuePresent(normalizedValue)) {
      nextFilters[key] = normalizedValue;
    }
  });

  return nextFilters;
};

export function removeFilterChip(
  filters: FileExplorerFilters,
  chip: ActiveFilterChip
): FileExplorerFilters {
  const nextFilters: FileExplorerFilters = {
    ...filters,
    tags: [...filters.tags],
  };

  if (chip.type === "tag") {
    nextFilters.tags = nextFilters.tags.filter((tag) => tag !== chip.value);
  } else if (chip.type === "name") {
    nextFilters.name = undefined;
  } else {
    const attributeKey = chip.sourceKey ?? chip.label;
    const attributeValue = nextFilters[attributeKey];

    if (Array.isArray(attributeValue)) {
      const remainingValues = attributeValue.filter(
        (val) => String(val) !== chip.value
      );

      if (remainingValues.length > 0) {
        nextFilters[attributeKey] = remainingValues;
      } else {
        delete nextFilters[attributeKey];
      }
    } else {
      delete nextFilters[attributeKey];
    }
  }

  return sanitizeFilters(nextFilters);
}

export function clearAllFileFilters(): FileExplorerFilters {
  return {
    tags: [],
  };
}

export function formatRelativeDate(dateString: string | undefined): string {
  if (!dateString) return "—";

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return "—";
  }
}

export function getThumbnailUrl(thumbnailUrl?: string): string | undefined {
  if (!thumbnailUrl) return undefined;
  if (
    thumbnailUrl.startsWith("http://") ||
    thumbnailUrl.startsWith("https://")
  ) {
    return thumbnailUrl;
  }
  return `${apiConfig.baseUrl}${thumbnailUrl}`;
}

export function extractAttributeOrder(files: FileItem[]): string[] {
  const firstFileWithAttributes = files.find(
    (file) => file.attributeValues && file.attributeValues.length > 0
  );

  if (!firstFileWithAttributes?.attributeValues) {
    return [];
  }

  const allAttributeNames = new Set<string>();
  files.forEach((file) => {
    file.attributeValues?.forEach((attr) => {
      allAttributeNames.add(attr.attributeName);
    });
  });

  const orderedAttributes: string[] = [];
  const seenAttributes = new Set<string>();

  firstFileWithAttributes.attributeValues.forEach((attr) => {
    if (allAttributeNames.has(attr.attributeName)) {
      orderedAttributes.push(attr.attributeName);
      seenAttributes.add(attr.attributeName);
    }
  });

  allAttributeNames.forEach((attrName) => {
    if (!seenAttributes.has(attrName)) {
      orderedAttributes.push(attrName);
    }
  });

  return orderedAttributes;
}

export function extractAttributeColumns(
  files: FileItem[],
  categoryAttributes?: Array<{ attributeName: string; order: number }>
): string[] {
  if (categoryAttributes && categoryAttributes.length > 0) {
    const orderMap = new Map(
      categoryAttributes.map((attr) => [attr.attributeName, attr.order])
    );

    const allCategoryAttributeNames = categoryAttributes.map(
      (attr) => attr.attributeName
    );

    return allCategoryAttributeNames.sort((a, b) => {
      const orderA = orderMap.get(a) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(b) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  return extractAttributeOrder(files);
}

export function isPdf(file: Pick<FileItem, "extension" | "contentType">): boolean {
  return (
    file.extension.toLowerCase() === ".pdf" ||
    file.contentType.toLowerCase() === "application/pdf"
  );
}

export function getFileActions(
  downloadFile: () => void,
  isDownloading: boolean,
  deleteFile?: () => void,
  isDeleting?: boolean
): FileActionButton[] {
  return [
    {
      icon: Tag,
      label: "Edit tags",
    },
    {
      icon: Download,
      label: "Download",
      onClick: downloadFile,
      disabled: isDownloading,
    },
    {
      icon: Trash2,
      label: "Delete",
      onClick: deleteFile,
      disabled: isDeleting,
      className: "text-red-600 hover:text-red-700 hover:bg-red-50",
    },
  ];
}
